<script setup>
import { computed, inject, onMounted, ref, watch } from "vue";
import { Dashboard, Entity, Review } from "@/services";
import { useMainStore } from "@/stores/main.js";
import { formatTrendAxis, formatTrendTip, mapPeriodToDateRange } from "@/utils";
import { scoreLabel } from "@/lib/score";
import MHeader from "@/components/m/MHeader.vue";
import MPullScroll from "@/components/m/MPullScroll.vue";
import MIcon from "@/components/m/MIcon.vue";
import MHeroGauge from "@/components/m/MHeroGauge.vue";
import MBars from "@/components/m/MBars.vue";
import MSpark from "@/components/m/MSpark.vue";
import MStars from "@/components/m/MStars.vue";
import BtSourceMark from "@/components/bt/BtSourceMark.vue";
import BtAreaTrend from "@/components/bt/BtAreaTrend.vue";
import MLocationScope from "@/components/m/MLocationScope.vue";
import MPeriodScope from "@/components/m/MPeriodScope.vue";

const ctx = inject("mctx");
const mainStore = useMainStore();

const SOURCE_LABELS = {
  GOOGLE: "Google",
  HELLOPETER: "Hellopeter",
  FACEBOOK: "Facebook",
  TRIPADVISOR: "TripAdvisor",
  FRESHA: "Fresha",
};
const sourceLabel = (v) =>
  SOURCE_LABELS[v] || (v ? v[0] + v.slice(1).toLowerCase() : "Unknown");

const ws = computed(() => ctx.workspace.value);
// activeCompanyId honours the read-only competitor view (falls back to the
// owned company when not observing a competitor).
const companyId = computed(() => mainStore.activeCompanyId);
const locationId = computed(() => {
  const loc = mainStore.selectedLocation;
  if (!loc || loc.location_id === "ALL") return null;
  return loc.location_id ?? null;
});
const dateRange = computed(() => {
  const { startDate, endDate } = mapPeriodToDateRange(
    mainStore.selectedPeriod,
    mainStore.customRange,
  );
  return { start: startDate.toISOString(), end: endDate.toISOString() };
});

const loading = ref(false);
const errored = ref(false);
const overview = ref(null);
const reputation = ref(null);
const priority = ref({ count: 0, items: [] });
const negTopics = ref([]);
const posTopics = ref([]);
const trend = ref({ labels: [], volumes: [], sentiments: [] });
const entityOverview = ref(null);
const sourceSentiment = ref([]);
const channelSeries = ref([]);

const NEGATIVE_SHADES = [
  "var(--neg)",
  "#E06A4A",
  "#E8902B",
  "#C9633E",
  "#A85C5C",
];
const POSITIVE_SHADES = [
  "var(--pos)",
  "#3FB58A",
  "#57B06E",
  "#7FC08F",
  "#9AC9A3",
];

/* ---- derived ---- */
const score = computed(() => Math.round(Number(reputation.value?.score ?? 0)));
const grade = computed(() => reputation.value?.grade ?? "-");
const scoreDelta = computed(() => {
  const t = reputation.value?.score_trend;
  return t === null || t === undefined ? null : Number(t);
});
const status = computed(() => scoreLabel(score.value));
const trail = computed(() => {
  // Only render a trail when the backend supplies real history. Previously we
  // fabricated a linear ramp toward the current score — that invented data, so
  // when there's no real trail we now render nothing (empty array).
  const raw = reputation.value?.score_trail || reputation.value?.trail;
  return Array.isArray(raw) && raw.length ? raw.map(Number) : [];
});
const T_MIN = 40;
const T_MAX = 100;
const trailHeight = (v) =>
  Math.max(4, ((v - T_MIN) / (T_MAX - T_MIN)) * 100) + "%";

function fmt(value, digits = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value)))
    return "-";
  return Number(value).toFixed(digits);
}
function trendStr(value, digits = 1, suffix = "") {
  if (value === null || value === undefined || Number.isNaN(Number(value)))
    return "—";
  const n = Number(value);
  return (n > 0 ? "+" : "") + n.toFixed(digits) + suffix;
}

/* ---- reputation factor breakdown ---- */
const factors = computed(() => reputation.value?.factors || []);

/* ---- review volume & sentiment trend ---- */
const trendLabels = computed(() =>
  trend.value.labels.map((v) =>
    formatTrendAxis(v, mainStore.selectedAggregation),
  ),
);
const trendTipLabels = computed(() =>
  trend.value.labels.map((v) =>
    formatTrendTip(v, mainStore.selectedAggregation),
  ),
);
const hasTrend = computed(() => trend.value.volumes.length > 1);

/* ---- topic helpers (mirror desktop mapTopics) ---- */
function mapTopics(rows, shades) {
  const list = Array.isArray(rows) ? rows : [];
  const total = list.reduce((acc, row) => acc + Number(row.count || 0), 0);
  const pctOf = (count) => (total ? Math.round((count / total) * 100) : 0);
  const items = list.slice(0, 5).map((row, index) => ({
    label: row.topic,
    value: Number(row.count) || 0,
    pct: pctOf(row.count),
    color: shades[index % shades.length],
  }));
  return { items, total };
}
const negMapped = computed(() => mapTopics(negTopics.value, NEGATIVE_SHADES));
const posMapped = computed(() => mapTopics(posTopics.value, POSITIVE_SHADES));

const pulse = computed(() => {
  const o = overview.value || {};
  const sentiment =
    o.avg_sentiment != null ? Math.round(Number(o.avg_sentiment) * 100) : null;
  return {
    reviews: {
      value: Number(o.reviews_count || 0).toLocaleString(),
      delta: trendStr(o.reviews_count_trend, 1, "%"),
      dir: Number(o.reviews_count_trend || 0) >= 0 ? "up" : "down",
    },
    rating: {
      value: fmt(o.avg_rating, 2),
      delta: trendStr(o.avg_rating_trend, 2),
      dir: Number(o.avg_rating_trend || 0) >= 0 ? "up" : "down",
    },
    sentiment: {
      value: sentiment == null ? "-" : (sentiment > 0 ? "+" : "") + sentiment,
      delta: trendStr(o.avg_sentiment_trend, 0),
      dir: Number(o.avg_sentiment_trend || 0) >= 0 ? "up" : "down",
    },
    reply: {
      value:
        o.negative_answered_pct == null
          ? "-"
          : `${Math.round(Number(o.negative_answered_pct))}%`,
      delta: trendStr(o.negative_answered_pct_trend, 1, "%"),
      dir: Number(o.negative_answered_pct_trend || 0) >= 0 ? "up" : "down",
    },
  };
});

const topComplaint = computed(() => negTopics.value[0]?.topic || null);

const complaintBars = computed(() =>
  negMapped.value.items.slice(0, 4).map((row) => ({
    label: row.label,
    v: row.value,
    pct: row.pct,
    c: row.color,
  })),
);
const positiveBars = computed(() =>
  posMapped.value.items.slice(0, 4).map((row) => ({
    label: row.label,
    v: row.value,
    pct: row.pct,
    c: row.color,
  })),
);

/* ---- AI recommendations (mirror desktop) ---- */
const recommendations = computed(() => {
  const recs = [];
  const topNegative = negMapped.value.items[0];
  if (topNegative) {
    recs.push({
      icon: "flame",
      tone: "neg",
      title: `“${topNegative.label}” is ${topNegative.pct}% of all complaints`,
      body: `${topNegative.value.toLocaleString()} negative mentions this period. Clear them from the priority inbox first.`,
      cta: "Open inbox",
      action: () => ctx.go("Reviews", { tab: 0 }),
    });
  }
  const responseHours = overview.value?.median_response_time_hrs;
  if (responseHours !== null && responseHours !== undefined) {
    recs.push({
      icon: "clock",
      tone: "brand",
      title: `Your typical reply time is ${fmt(responseHours, 1)}h`,
      body: "Faster replies lift ratings ~0.4★ on average. Acknowledge urgent reviews first.",
      cta: "Open inbox",
      action: () => ctx.go("Reviews", { tab: 0 }),
    });
  }
  const bestChannel = channels.value.find((c) => c.dir === "up");
  const topPositive = posMapped.value.items[0];
  if (bestChannel && topPositive) {
    recs.push({
      icon: "trending",
      tone: "pos",
      title: `Positive momentum on ${bestChannel.label}`,
      body: `${bestChannel.label} review volume rose ${bestChannel.delta} over the period. Ask your ${topPositive.value.toLocaleString()} happy customers for a star rating.`,
      cta: "See topic",
      action: () => ctx.go("Reviews", { topic: topPositive.label }),
    });
  }
  return recs.slice(0, 3);
});
const recTones = {
  neg: ["var(--neg-soft)", "var(--neg)"],
  brand: ["var(--brand-soft)", "var(--brand-strong)"],
  pos: ["var(--pos-soft)", "var(--pos)"],
};

/* ---- entity overview (products / people / places) ---- */
const ENTITY_GROUPS = [
  ["PRODUCT", "Products / Services"],
  ["PERSON", "People"],
  ["PLACE", "Places"],
];

const channels = computed(() => {
  const grandTotal = sourceSentiment.value.reduce(
    (acc, r) =>
      acc +
      Number(r.PositiveCount || 0) +
      Number(r.NegativeCount || 0) +
      Number(r.NeutralCount || 0),
    0,
  );
  const seriesFor = (src) =>
    channelSeries.value.find((s) => s.label === src)?.data || [];
  return sourceSentiment.value
    .map((r) => {
      const total =
        Number(r.PositiveCount || 0) +
        Number(r.NegativeCount || 0) +
        Number(r.NeutralCount || 0);
      const series = seriesFor(r.source).map(Number);
      const first = series.find((v) => v > 0) || series[0] || 0;
      const last = series.length ? series[series.length - 1] : 0;
      const delta = first ? Math.round(((last - first) / first) * 100) : 0;
      return {
        source: r.source,
        label: sourceLabel(r.source),
        reviews: total,
        rating: r.AvgRating != null ? Number(r.AvgRating) : null,
        spark: series.length ? series : [0, 0],
        delta: (delta >= 0 ? "+" : "") + delta + "%",
        dir: delta >= 0 ? "up" : "down",
      };
    })
    .sort((a, b) => b.reviews - a.reviews);
});

const inboxItems = computed(() => priority.value.items.slice(0, 3));

async function fetchHome() {
  if (!companyId.value) return;
  loading.value = true;
  errored.value = false;
  try {
    const locId = locationId.value;
    const start = dateRange.value.start;
    const end = dateRange.value.end;
    const [
      stats,
      trendData,
      scoreData,
      priorityData,
      negative,
      positive,
      sentimentBySource,
      platformVolume,
      entityData,
    ] = await Promise.all([
      Review.getOverviewStatsByCompanyID(companyId.value, start, end, locId),
      Dashboard.getReviewTrend(
        companyId.value,
        mainStore.selectedAggregation.toLowerCase(),
        start,
        end,
        locId,
      ),
      Dashboard.getReputationScore(companyId.value, start, end, locId),
      Dashboard.getPriorityReviews(companyId.value, start, end, 3, locId),
      Review.getNegativeTopics(companyId.value, start, end, locId),
      Review.getPositiveTopics(companyId.value, start, end, locId),
      Review.getSentimentBySource(companyId.value, start, end, locId),
      Review.getCountByPlatformByMonth(
        companyId.value,
        "monthly",
        start,
        end,
        locId,
      ),
      // Entity.overview throws on error (no internal catch) — guard it so a 500
      // here doesn't reject the whole Promise.all.
      Entity.overview(companyId.value, 5, start, end, locId).catch(() => null),
    ]);
    overview.value = stats && !Array.isArray(stats) ? stats : null;
    const sentimentSeries =
      trendData?.series?.find((s) => s.name === "Sentiment")?.data || [];
    const volumeSeries =
      trendData?.series?.find((s) => s.name === "Reviews")?.data || [];
    trend.value = {
      labels: trendData?.dates || [],
      volumes: volumeSeries,
      sentiments: sentimentSeries,
    };
    reputation.value = scoreData || null;
    priority.value = priorityData || { count: 0, items: [] };
    negTopics.value = Array.isArray(negative) ? negative : [];
    posTopics.value = Array.isArray(positive) ? positive : [];
    sourceSentiment.value = sentimentBySource || [];
    channelSeries.value = platformVolume?.series || [];
    entityOverview.value = entityData || null;
    ctx.setUrgent(priority.value.count || null);
  } catch (error) {
    console.error("Failed to load mobile home dashboard:", error);
    errored.value = true;
  } finally {
    loading.value = false;
  }
}

watch(
  () => [
    companyId.value,
    mainStore.dateFilterKey,
    mainStore.selectedAggregation,
    mainStore.selectedLocation?.location_id,
  ],
  fetchHome,
);
onMounted(fetchHome);
</script>

<template>
  <div class="btm-app">
    <MPullScroll :busy="loading" @refresh="fetchHome">
      <MHeader title="Good morning" :sub="ws.name" />

      <MPeriodScope />

      <div
        v-if="mainStore.hasMultipleBranches"
        class="btm-pad"
        style="padding-bottom: 12px"
      >
        <MLocationScope />
      </div>

      <div class="btm-pad">
        <!-- HERO reputation spotlight -->
        <div class="btm-hero btm-rise" style="margin-bottom: 14px">
          <div style="display: flex; align-items: center; margin-bottom: 16px">
            <span class="eb">Reputation Score</span>
          </div>
          <div style="display: flex; align-items: center; gap: 16px">
            <MHeroGauge :key="score" :value="score" :grade="grade" />
            <div style="flex: 1; min-width: 0">
              <div class="status">{{ status }}</div>
              <div
                style="
                  display: flex;
                  align-items: center;
                  gap: 7px;
                  margin: 8px 0 14px;
                "
              >
                <span
                  v-if="scoreDelta != null"
                  class="btm-hero-pill"
                  :style="
                    scoreDelta < 0
                      ? { background: 'rgba(232,115,110,.2)', color: '#F0A6A2' }
                      : null
                  "
                  ><MIcon
                    :name="scoreDelta >= 0 ? 'arrowupright' : 'arrowdownright'"
                    :size="11"
                    :sw="2.6"
                  />{{ Math.abs(scoreDelta) }} pts</span
                >
                <span
                  style="
                    font-size: 11.5px;
                    color: rgba(246, 238, 223, 0.6);
                    white-space: nowrap;
                  "
                  >this period</span
                >
              </div>
              <div v-if="trail.length" class="btm-hero-trail">
                <i
                  v-for="(v, i) in trail"
                  :key="i"
                  :style="{
                    height: trailHeight(v),
                    opacity: 0.45 + 0.55 * (i / Math.max(1, trail.length - 1)),
                  }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Reputation factor breakdown -->
        <div
          v-if="factors.length"
          class="btm-card btm-cardpad btm-rise"
          style="margin-bottom: 14px"
        >
          <div class="btm-eyebrow" style="margin-bottom: 12px">
            Score breakdown
          </div>
          <div style="display: flex; flex-direction: column; gap: 11px">
            <div
              v-for="factor in factors"
              :key="factor.label"
              class="btm-factor"
            >
              <span class="lab">{{ factor.label }}</span>
              <span class="btm-meter"
                ><span
                  :style="{
                    width: `${Math.round(factor.value)}%`,
                    background: factor.color,
                  }"
              /></span>
              <span class="btm-mono val">{{ Math.round(factor.value) }}</span>
            </div>
          </div>
        </div>

        <!-- PULSE strip -->
        <div
          class="btm-pulse btm-rise"
          style="margin-bottom: 14px; grid-template-columns: repeat(2, 1fr)"
        >
          <div>
            <div class="k">
              <MIcon
                name="reviews"
                :size="12"
                style="color: var(--brand-strong)"
              />Reviews
            </div>
            <div class="v btm-num">{{ pulse.reviews.value }}</div>
            <div style="margin-top: 5px">
              <span
                class="btm-delta"
                :class="pulse.reviews.dir"
                style="font-size: 10px; padding: 1px 6px 1px 4px"
                ><MIcon
                  :name="
                    pulse.reviews.dir === 'up'
                      ? 'arrowupright'
                      : 'arrowdownright'
                  "
                  :size="9"
                  :sw="2.6"
                />{{ pulse.reviews.delta }}</span
              >
            </div>
          </div>
          <div>
            <div class="k">
              <MIcon name="star" :size="12" style="color: var(--pos)" />Rating
            </div>
            <div class="v btm-num">
              {{ pulse.rating.value }}<span class="s">★</span>
            </div>
            <div style="margin-top: 5px">
              <span
                class="btm-delta"
                :class="pulse.rating.dir"
                style="font-size: 10px; padding: 1px 6px 1px 4px"
                ><MIcon
                  :name="
                    pulse.rating.dir === 'up'
                      ? 'arrowupright'
                      : 'arrowdownright'
                  "
                  :size="9"
                  :sw="2.6"
                />{{ pulse.rating.delta }}</span
              >
            </div>
          </div>
          <div>
            <div class="k">
              <MIcon
                name="heart"
                :size="12"
                style="color: var(--info)"
              />Sentiment
            </div>
            <div class="v btm-num">{{ pulse.sentiment.value }}</div>
            <div style="margin-top: 5px">
              <span
                class="btm-delta"
                :class="pulse.sentiment.dir"
                style="font-size: 10px; padding: 1px 6px 1px 4px"
                ><MIcon
                  :name="
                    pulse.sentiment.dir === 'up'
                      ? 'arrowupright'
                      : 'arrowdownright'
                  "
                  :size="9"
                  :sw="2.6"
                />{{ pulse.sentiment.delta }}</span
              >
            </div>
          </div>
          <div>
            <div class="k">
              <MIcon name="clock" :size="12" style="color: var(--neg)" />Neg.
              Answered
            </div>
            <div class="v btm-num">
              {{ pulse.reply.value }}
            </div>
            <div style="margin-top: 5px">
              <span
                class="btm-delta"
                :class="pulse.reply.dir"
                style="font-size: 10px; padding: 1px 6px 1px 4px"
                ><MIcon
                  :name="
                    pulse.reply.dir === 'up' ? 'arrowupright' : 'arrowdownright'
                  "
                  :size="9"
                  :sw="2.6"
                />{{ pulse.reply.delta }}</span
              >
            </div>
          </div>
        </div>

        <!-- Review volume & sentiment trend -->
        <div class="btm-card btm-cardpad btm-rise" style="margin-bottom: 14px">
          <div
            style="
              display: flex;
              align-items: center;
              margin-bottom: 10px;
              gap: 8px;
            "
          >
            <span class="btm-seclabel-t">Volume &amp; Sentiment</span>
            <div style="display: flex; gap: 10px; margin-left: auto">
              <span class="btm-trend-chip"
                ><span
                  class="dot"
                  style="background: var(--brand)"
                />Volume</span
              >
              <span class="btm-trend-chip"
                ><span
                  class="dot"
                  style="background: var(--pos)"
                />Sentiment</span
              >
            </div>
          </div>
          <BtAreaTrend
            v-if="hasTrend"
            :volumes="trend.volumes"
            :sentiments="trend.sentiments"
            :labels="trendLabels"
            :tip-labels="trendTipLabels"
            :width="340"
            :height="200"
          />
          <div
            v-else
            class="btm-muted"
            style="padding: 22px 0; text-align: center"
          >
            Not enough data to chart a trend yet.
          </div>
        </div>

        <!-- AI recommendations -->
        <div class="btm-assist btm-rise" style="margin-bottom: 4px">
          <div class="btm-assist-top">
            <span class="btm-assist-ava"
              ><MIcon name="sparkles" :size="19" fill
            /></span>
            <div style="flex: 1; min-width: 0">
              <div
                style="
                  display: flex;
                  align-items: center;
                  gap: 7px;
                  margin-bottom: 4px;
                "
              >
                <span style="font-weight: 800; font-size: 13.5px">BuzzAI</span>
                <span class="btm-tag brand"
                  ><MIcon name="sparkles" :size="10" fill />Recommends</span
                >
                <span
                  v-if="priority.count"
                  class="btm-tag neg"
                  style="margin-left: auto"
                  ><MIcon name="flame" :size="10" fill />{{
                    priority.count
                  }}
                  urgent</span
                >
              </div>
              <div class="btm-muted" style="font-size: 11.5px">
                {{ recommendations.length }} action{{
                  recommendations.length === 1 ? "" : "s"
                }}
                to lift your score this period
              </div>
            </div>
          </div>
          <div class="btm-rec-list">
            <div
              v-for="rec in recommendations"
              :key="rec.title"
              class="btm-rec"
            >
              <span
                class="btm-rec-ico"
                :style="{
                  background: recTones[rec.tone][0],
                  color: recTones[rec.tone][1],
                }"
                ><MIcon :name="rec.icon" :size="16" fill
              /></span>
              <div style="flex: 1; min-width: 0">
                <div class="btm-rec-title">{{ rec.title }}</div>
                <div class="btm-rec-body">{{ rec.body }}</div>
                <button
                  class="btm-btn btm-btn-ghost btm-btn-sm"
                  style="margin-top: 9px"
                  @click="rec.action"
                >
                  {{ rec.cta }}<MIcon name="chevright" :size="13" />
                </button>
              </div>
            </div>
            <div
              v-if="!recommendations.length"
              class="btm-muted"
              style="padding: 4px 0"
            >
              Not enough data yet to generate recommendations.
            </div>
          </div>
        </div>
      </div>

      <!-- Channels rail -->
      <div class="btm-seclabel btm-pad">
        <span class="t">Channels</span>
        <button type="button" class="l" @click="ctx.go('Reviews')">
          All <MIcon name="chevright" :size="13" />
        </button>
      </div>
      <div class="btm-rail btm-rise">
        <div
          v-for="c in channels"
          :key="c.source"
          class="btm-card btm-cardpad"
          style="width: 172px; cursor: pointer"
          @click="ctx.go('Reviews', { source: c.source })"
        >
          <div
            style="
              display: flex;
              align-items: center;
              gap: 9px;
              margin-bottom: 12px;
            "
          >
            <BtSourceMark :source="c.source" :size="32" />
            <div style="flex: 1; min-width: 0">
              <div style="font-weight: 800; font-size: 13.5px">
                {{ c.label }}
              </div>
              <div class="btm-muted" style="font-size: 11px">
                {{ c.reviews.toLocaleString() }} reviews
              </div>
            </div>
          </div>
          <div
            style="
              display: flex;
              align-items: center;
              gap: 7px;
              margin-bottom: 10px;
            "
          >
            <MStars :value="c.rating || 0" :size="13" />
            <span
              class="btm-num btm-mono"
              style="font-size: 13px; font-weight: 800"
              >{{ c.rating != null ? c.rating.toFixed(1) : "-" }}</span
            >
            <span class="btm-delta" :class="c.dir" style="margin-left: auto"
              ><MIcon
                :name="c.dir === 'up' ? 'arrowupright' : 'arrowdownright'"
                :size="10"
                :sw="2.4"
              />{{ c.delta }}</span
            >
          </div>
          <MSpark
            :data="c.spark"
            :w="140"
            :h="26"
            :color="c.dir === 'up' ? 'var(--pos)' : 'var(--neg)'"
            :fillc="c.dir === 'up' ? 'var(--pos-soft)' : 'var(--neg-soft)'"
          />
        </div>
        <div
          v-if="!channels.length"
          class="btm-muted btm-card btm-cardpad"
          style="width: 200px"
        >
          No connected channels yet.
        </div>
      </div>

      <!-- Top complaints -->
      <div class="btm-seclabel btm-pad">
        <span class="t">Top Complaints</span>
        <button type="button" class="l" @click="ctx.go('Topics')">
          Topics <MIcon name="chevright" :size="13" />
        </button>
      </div>
      <div class="btm-pad btm-rise">
        <div class="btm-card btm-cardpad">
          <MBars
            v-if="complaintBars.length"
            :items="complaintBars"
            @pick="(label) => ctx.go('Reviews', { topic: label })"
          />
          <div v-else class="btm-muted">
            No negative topics in this period. 🎉
          </div>
        </div>
      </div>

      <!-- Top praise -->
      <div class="btm-seclabel btm-pad">
        <span class="t">Top Praise</span>
        <button type="button" class="l" @click="ctx.go('Topics')">
          Topics <MIcon name="chevright" :size="13" />
        </button>
      </div>
      <div class="btm-pad btm-rise">
        <div class="btm-card btm-cardpad">
          <MBars
            v-if="positiveBars.length"
            :items="positiveBars"
            @pick="(label) => ctx.go('Reviews', { topic: label })"
          />
          <div v-else class="btm-muted">No positive topics in this period.</div>
        </div>
      </div>

      <!-- Priority inbox preview -->
      <div class="btm-seclabel btm-pad">
        <span class="t">Needs a Reply</span>
        <button type="button" class="l" @click="ctx.go('Reviews', { tab: 0 })">
          {{ priority.count }} urgent <MIcon name="chevright" :size="13" />
        </button>
      </div>
      <div class="btm-pad btm-rise" style="padding-bottom: 18px">
        <div class="btm-card btm-cardpad">
          <div
            v-for="(r, i) in inboxItems"
            :key="r.id || i"
            class="btm-trow"
            @click="ctx.go('Reviews', { tab: 0 })"
          >
            <BtSourceMark :source="r.source" :size="30" />
            <div style="flex: 1; min-width: 0">
              <div
                style="
                  display: flex;
                  align-items: center;
                  gap: 7px;
                  margin-bottom: 2px;
                "
              >
                <MStars :value="r.rating" :size="11" />
                <span
                  style="
                    font-weight: 700;
                    font-size: 12.5px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  "
                  >{{ r.topic || r.emotion || "Review" }}</span
                >
              </div>
              <div
                style="
                  font-size: 12px;
                  color: var(--text-2);
                  line-height: 1.4;
                  display: -webkit-box;
                  -webkit-line-clamp: 1;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                "
              >
                <template v-if="r.content">{{ r.content }}</template>
                <span v-else style="font-style: italic; opacity: 0.7"
                  >Rating only — no written comment</span
                >
              </div>
            </div>
          </div>
          <div
            v-if="!inboxItems.length"
            class="btm-muted"
            style="padding: 6px 0"
          >
            You're all caught up. 🎉
          </div>
        </div>
      </div>

      <!-- Top entities -->
      <template v-if="entityOverview">
        <div class="btm-seclabel btm-pad">
          <span class="t">Top Entities</span>
          <button type="button" class="l" @click="ctx.go('Entities')">
            All <MIcon name="chevright" :size="13" />
          </button>
        </div>
        <div class="btm-pad btm-rise" style="padding-bottom: 18px">
          <div class="btm-card btm-cardpad">
            <template
              v-for="([typeKey, typeLabel], gi) in ENTITY_GROUPS"
              :key="typeKey"
            >
              <div
                v-if="(entityOverview[typeKey] || []).length"
                :style="gi ? 'margin-top: 14px' : null"
              >
                <div class="btm-eyebrow">{{ typeLabel }}</div>
                <div
                  v-for="item in (entityOverview[typeKey] || []).slice(0, 5)"
                  :key="item.label"
                  class="btm-entrow"
                  @click="ctx.go('Entities', { entity: item.label })"
                >
                  <span class="name">{{ item.label }}</span>
                  <span class="btm-mono" style="color: var(--text-3)">{{
                    Number(item.mention_count || 0).toLocaleString()
                  }}</span>
                  <span
                    v-if="(item.positive || 0) > (item.negative || 0)"
                    class="btm-tag pos"
                    style="height: 18px; font-size: 9px; padding: 0 6px"
                    >+</span
                  >
                  <span
                    v-else-if="(item.negative || 0) > (item.positive || 0)"
                    class="btm-tag neg"
                    style="height: 18px; font-size: 9px; padding: 0 6px"
                    >−</span
                  >
                  <span
                    v-else
                    class="btm-tag neu"
                    style="height: 18px; font-size: 9px; padding: 0 6px"
                    >~</span
                  >
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>

      <!-- Loading / error states -->
      <div
        v-if="loading"
        class="btm-pad btm-muted"
        style="padding-bottom: 18px"
      >
        <MIcon name="loader" :size="13" /> Refreshing…
      </div>
      <div v-else-if="errored" class="btm-pad" style="padding-bottom: 18px">
        <div class="btm-card btm-cardpad btm-muted" style="text-align: center">
          Couldn't load your dashboard.
          <button
            class="btm-btn btm-btn-ghost btm-btn-sm"
            style="margin-top: 10px"
            @click="fetchHome"
          >
            Retry
          </button>
        </div>
      </div>
    </MPullScroll>
  </div>
</template>

<style scoped>
.btm-seclabel-t {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.btm-trend-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 700;
  color: var(--text-2);
}
.btm-trend-chip .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.btm-rec-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 15px 15px;
}
.btm-rec {
  display: flex;
  gap: 11px;
  align-items: flex-start;
}
.btm-rec + .btm-rec {
  padding-top: 14px;
  border-top: 1px solid var(--border);
}
.btm-rec-ico {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: grid;
  place-items: center;
}
.btm-rec-title {
  font-weight: 800;
  font-size: 13px;
  line-height: 1.35;
  margin-bottom: 4px;
}
.btm-rec-body {
  font-size: 12.5px;
  color: var(--text-2);
  line-height: 1.5;
}
.btm-factor {
  display: grid;
  grid-template-columns: 92px 1fr 28px;
  gap: 10px;
  align-items: center;
}
.btm-factor .lab {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btm-factor .val {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--text-2);
  text-align: right;
}
.btm-entrow {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
}
.btm-entrow:last-child {
  border-bottom: 0;
}
.btm-entrow .name {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
